import { timeToMinutes } from "./timeParsing";
import { TimeFormat } from "../types";

/**
 * Checks if a time aligns with a given step interval.
 *
 * @param time - The time to check
 * @param step - The step increment in minutes
 * @returns True if the time aligns with the step
 */
export function isTimeOnStep(time: string, step: number): boolean {
  if (!step || step <= 0) return true;

  const totalMinutes = timeToMinutes(time);
  return totalMinutes % step === 0;
}

/**
 * Checks if a time is valid (exists and can be parsed).
 *
 * @param time - The time to check
 * @returns True if the time is valid
 */
export function isValidTime(time: string | null): boolean {
  if (!time) return false;
  return timeToMinutes(time) > 0 || time === "00:00";
}

/**
 * Checks if a partial input might become valid as the user continues typing.
 * For example, "1" in a 10-11 AM range could become "10:00 AM" or "11:00 AM".
 * Or "2" in a 20:00-23:00 range could become "20:00", "21:00", etc.
 *
 * @param inputValue - The current input value (may include mask characters)
 * @param format - The time format (12 or 24 hour)
 * @param min - Minimum allowed time constraint
 * @param max - Maximum allowed time constraint
 * @returns True if the partial input could potentially become valid
 */
export function isPartialInputPotentiallyValid(
  inputValue: string,
  format: TimeFormat,
  min?: string,
  max?: string,
): boolean {
  // Must have both constraints or neither
  if (!min || !max) return false;

  // Extract just the digits from the input
  const digits = inputValue.replace(/[^\d]/g, "");

  // Only handle single digit case - check if it could start a valid hour
  if (digits.length === 1) {
    const digit = parseInt(digits, 10);

    // Convert constraints to minutes for easier comparison
    const minMinutes = timeToMinutes(min);
    const maxMinutes = timeToMinutes(max);

    // Check all possible hours this digit could represent
    for (let h = 0; h <= 23; h++) {
      // Check if this hour starts with our digit (check both padded and unpadded)
      const hourStr = h.toString().padStart(2, "0");
      const hourStrNoPad = h.toString();

      const startsWithDigit =
        hourStr.startsWith(digit.toString()) ||
        hourStrNoPad.startsWith(digit.toString());

      if (!startsWithDigit) continue;

      if (format === 24) {
        // 24-hour format - simple check
        const hourMinutes = h * 60;
        if (hourMinutes >= minMinutes && hourMinutes <= maxMinutes) {
          return true;
        }
      } else {
        // 12-hour format - check both AM and PM versions
        if (h === 0) {
          // Hour 0 = 12 AM (midnight)
          if (digit === 1) {
            // "1" could be "12 AM"
            const midnightMinutes = 0;
            if (
              midnightMinutes >= minMinutes &&
              midnightMinutes <= maxMinutes
            ) {
              return true;
            }
          }
        } else if (h >= 1 && h <= 12) {
          // Hours 1-12: check both AM and PM

          // AM version
          const amHour24 = h === 12 ? 0 : h; // 12 AM = hour 0, others stay same
          const amMinutes = amHour24 * 60;
          if (amMinutes >= minMinutes && amMinutes <= maxMinutes) {
            return true;
          }

          // PM version
          const pmHour24 = h === 12 ? 12 : h + 12; // 12 PM = hour 12, others add 12
          const pmMinutes = pmHour24 * 60;
          if (pmMinutes >= minMinutes && pmMinutes <= maxMinutes) {
            return true;
          }
        }
      }
    }
  }

  return false;
}
